import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../../atoms/Card';
import Button from '../../atoms/Button';
import Icon from '../../atoms/Icon/Icon';
import { OrderOut } from '../../../types/api';
import { apiService } from '../../../services/api';
import { QRCodeUtils } from '../../../utils/qrCodeUtils';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { usePriceFormat } from '../../../hooks/usePriceFormat';
import OrderActivityLog from '../../molecules/OrderActivityLog';
import './OrderDetail.scss';

interface OrderDetailProps {
  order: OrderOut | null;
  className?: string;
  onOrderUpdate?: (updatedOrder: OrderOut) => void;
  eventName?: string;
  venueName?: string | null;
}

const OrderDetail: React.FC<OrderDetailProps> = ({
  order,
  className = '',
  onOrderUpdate,
  eventName,
  venueName
}) => {
  const { t } = useTranslation();
  const { formatPrice, formatWithTax } = usePriceFormat();
  const { formatPurchaseDateTime } = useDateFormatter();
  const [isUpdating, setIsUpdating] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setConfirmingCancel(false);
    setActionError(null);
  }, [order?.id]);

  if (!order) {
    return (
      <Card className={`order-detail ${className}`}>
        <div className="order-detail__empty">
          <Icon name="pedidos" size={20} alt={t('bartender.orderDetail.selectOrder')} /> {t('bartender.orderDetail.selectOrder')}
        </div>
      </Card>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'warning';
      case 'preparing': return 'primary';
      case 'ready': return 'success';
      case 'completed': return 'success';
      case 'paid': return 'success';
      case 'cancelled': return 'error';
      default: return 'secondary';
    }
  };

  const createdAt = formatPurchaseDateTime(order.created_at);
  const updatedAt = formatPurchaseDateTime(order.updated_at);

  // Cancelling lives only here, in the order's detail, and not in the
  // delivery workflow at the bar, so an order is never cancelled by mistake
  // while serving. Delivered or already cancelled orders can't be cancelled.
  const canCancel = !['cancelled', 'completed'].includes(order.status.toLowerCase());

  const handleCancel = async () => {
    setIsUpdating(true);
    setActionError(null);
    try {
      const updatedOrder = await apiService.updateOrderStatus(order.id, 'cancelled');
      setConfirmingCancel(false);
      onOrderUpdate?.(updatedOrder);
    } catch (error: any) {
      console.error('Failed to cancel order:', error);
      setActionError(error?.response?.data?.detail || t('bartender.orders.updateError'));
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card className={`order-detail ${className}`}>
      <div className="order-detail__header">
        <div className="order-detail__title-row">
          <h3 className="order-detail__title">
            <Icon name="pedidos" size={20} alt={t('bartender.orderDetail.title')} /> {t('bartender.orderDetail.title')} #{order.id}
          </h3>
          <div className={`order-detail__status order-detail__status--${getStatusColor(order.status)}`}>
            {t(`orderConfirmation.statuses.${order.status.toLowerCase()}`)}
          </div>
        </div>
      </div>

      <div className="order-detail__content">
        
        <div className="order-detail__section">
          
          <h4 className="order-detail__section-title">
            {t('bartender.orderDetail.items')}
          </h4>

          <div className="order-detail__items">
            {order.items.map((item, index) => (
              <div key={index} className="order-detail__item">
                <div className="order-detail__item-header">
                  <div className="order-detail__item-name">
                    {item.quantity} &times; {item.drink_name || item.food_name}
                  </div>
                  <div className="order-detail__item-type">
                    <Icon name={item.item_type === 'drink' ? 'vasos' : 'cubiertos'} size={20} alt={item.item_type} />
                  </div>
                </div>
                {/*   
                <div className="order-detail__item-details">
                  <div className="order-detail__item-quantity">
                    <strong>{t('bartender.orderDetail.quantity')}: {item.quantity}</strong>
                  </div>         
                  <div className="order-detail__item-price">
                    ${item.price_at_time.toFixed(2)} {t('common.each')}
                  </div>
                  <div className="order-detail__item-total">
                    {t('common.total')}: ${(item.price_at_time * item.quantity).toFixed(2)} 
                  </div> 
                </div>
                */}
              </div>
            ))}
          </div>
        </div>

        <div className="order-detail__section">
          <div className="order-detail__metadata">
            {(eventName || order.event_name) && (
              <div className="order-detail__metadata-item">
                <span className="order-detail__metadata-label">
                  {t('bartender.orderDetail.event', { defaultValue: 'Evento' })}:
                </span>
                <span className="order-detail__metadata-value">
                  {eventName || order.event_name}
                </span>
              </div>
            )}
            {(venueName || order.venue_name) && (
            <div className="order-detail__metadata-item">
              <span className="order-detail__metadata-label">
                {t('bartender.orderDetail.venue', { defaultValue: 'Barra' })}:
              </span>
              <span className="order-detail__metadata-value">
                {venueName || order.venue_name}
              </span>
            </div>
            )}
            {order.claimed_by_name && (
              <div className="order-detail__metadata-item">
                <span className="order-detail__metadata-label">
                  {order.status === 'completed' ? t('orderActivity.deliveredBy') : t('orderActivity.openedBy')}:
                </span>
                <span className="order-detail__metadata-value">
                  {order.claimed_by_name}
                </span>
              </div>
            )}
            <div className="order-detail__metadata-item">
              <span className="order-detail__metadata-label">
                {t('bartender.orderDetail.created')}:
              </span>
              <span className="order-detail__metadata-value">
                {createdAt.date} {createdAt.time}
              </span>
            </div>
            
            <div className="order-detail__metadata-item">
              <span className="order-detail__metadata-label">
                {t('bartender.orderDetail.updated')}:
              </span>
              <span className="order-detail__metadata-value">
                {updatedAt.date} {updatedAt.time}
              </span>
            </div>

            <div className="order-detail__metadata-item">
              <span className="order-detail__metadata-label">
                {t('bartender.orderDetail.customer')}:
              </span>
              <span className="order-detail__metadata-value">
                {order.customer_id ? '#' + order.customer_id : 'N/A'}
              </span>
            </div>

            {order.customer && (
              <>
                {(order.customer.first_name || order.customer.last_name) && (
                  <div className="order-detail__metadata-item">
                    <span className="order-detail__metadata-label">
                      {t('bartender.orderDetail.customerName')}:
                    </span>
                    <span className="order-detail__metadata-value">
                      {[order.customer.first_name, order.customer.last_name].filter(Boolean).join(' ')}
                    </span>
                  </div>
                )}
                <div className="order-detail__metadata-item">
                  <span className="order-detail__metadata-label">
                    {t('bartender.orderDetail.customerEmail')}:
                  </span>
                  <span className="order-detail__metadata-value">
                    {order.customer.email}
                  </span>
                </div>
                {order.customer.phone && (
                  <div className="order-detail__metadata-item">
                    <span className="order-detail__metadata-label">
                      {t('bartender.orderDetail.customerPhone')}:
                    </span>
                    <span className="order-detail__metadata-value">
                      {order.customer.phone}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Payment Section */}
        <div className="order-detail__section">
          <h4 className="order-detail__section-title">
            {t('bartender.orderDetail.paymentInfo')}
          </h4>
          {order.card_brand || order.card_last4 || order.stripe_payment_intent_id ? (
            <div className="order-detail__metadata">
              {(order.card_brand || order.card_last4) && (
                <div className="order-detail__metadata-item">
                  <span className="order-detail__metadata-label">
                    {t('bartender.orderDetail.paymentMethod')}:
                  </span>
                  <span className="order-detail__metadata-value">
                    {[order.card_brand, order.card_last4 ? `•••• ${order.card_last4}` : null]
                      .filter(Boolean)
                      .join(' ')}
                  </span>
                </div>
              )}
              {order.stripe_payment_intent_id && (
                <div className="order-detail__metadata-item">
                  <span className="order-detail__metadata-label">
                    {t('bartender.orderDetail.paymentIntent')}:
                  </span>
                  <span className="order-detail__metadata-value">
                    {order.stripe_payment_intent_id}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="order-detail__empty-note">
              {t('bartender.orderDetail.noPaymentData')}
            </div>
          )}
        </div>

        {/* QR Security Section */}
        <div className="order-detail__section">
          <h4 className="order-detail__section-title">
            {t('bartender.orderDetail.qrSecurity')}
          </h4>
          <div className="order-detail__metadata">
            <div className="order-detail__metadata-item">
              <span className="order-detail__metadata-label">
                {t('bartender.orderDetail.qrUsageCount')}:
              </span>
              <span className="order-detail__metadata-value">
                {order.qr_code_usage_count ?? 0}
              </span>
            </div>
            {order.qr_code_last_used_at && (
              <div className="order-detail__metadata-item">
                <span className="order-detail__metadata-label">
                  {t('bartender.orderDetail.qrLastUsed')}:
                </span>
                <span className="order-detail__metadata-value">
                  {formatPurchaseDateTime(order.qr_code_last_used_at).date}{' '}
                  {formatPurchaseDateTime(order.qr_code_last_used_at).time}
                </span>
              </div>
            )}
            <div className="order-detail__metadata-item">
              <span className="order-detail__metadata-label">
                {t('bartender.orderDetail.qrLocked')}:
              </span>
              <span className="order-detail__metadata-value">
                {order.qr_code_locked
                  ? (order.qr_code_locked_reason || t('bartender.orderDetail.qrLocked'))
                  : t('bartender.orderDetail.qrNotLocked')}
              </span>
            </div>
          </div>
        </div>

        {/* Activity Log Section */}
        <div className="order-detail__section">
          <h4 className="order-detail__section-title">
            {t('bartender.orderDetail.activityLog')}
          </h4>
          <OrderActivityLog orderId={order.id} refreshKey={order.updated_at} />
        </div>

        {canCancel && (
          <div className="order-detail__section">
            <h4 className="order-detail__section-title">
              {t('bartender.orderDetail.actions')}
            </h4>
            {actionError && <div className="order-detail__empty-note">{actionError}</div>}
            {confirmingCancel ? (
              <div className="order-detail__confirm">
                <p className="order-detail__confirm-text">{t('bartender.orderDetail.cancelQuestion')}</p>
                <div className="order-detail__actions">
                  <Button
                    onClick={handleCancel}
                    variant="error"
                    size="lg"
                    loading={isUpdating}
                    disabled={isUpdating}
                    className="order-detail__action-btn"
                  >
                    {t('bartender.orderDetail.cancelConfirm')}
                  </Button>
                  <Button
                    onClick={() => setConfirmingCancel(false)}
                    variant="outline"
                    size="lg"
                    disabled={isUpdating}
                    className="order-detail__action-btn"
                  >
                    {t('bartender.orderDetail.cancelKeep')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="order-detail__actions">
                <Button
                  onClick={() => setConfirmingCancel(true)}
                  variant="error"
                  size="lg"
                  className="order-detail__action-btn"
                >
                  {t('bartender.orders.cancel')}
                </Button>
              </div>
            )}
          </div>
        )}

      </div>
    </Card>
  );
};

export default OrderDetail;
